import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, IndianRupee } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import {
  getEngageSourceBadge, ENGAGE_SOURCE_LABEL,
} from '../../../utils/engageStageBadge';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { filterByRange } from './ReportFilters';


const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });


export default function SourceROIReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [camps, setCamps] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [eResp, cResp, mResp] = await Promise.all([
          apiFetch('engage/enquiries/'),
          apiFetch('engage/health-camps/'),
          apiFetch('engage/digital-marketing-campaigns/'),
        ]);
        setEnquiries(eResp?.results || eResp?.data?.results || []);
        setCamps(cResp?.results || cResp?.data?.results || []);
        setCampaigns(mResp?.results || mResp?.data?.results || []);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load ROI data');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const rows = useMemo(() => {
    // Filter enquiries by date range first
    const enq = filterByRange(enquiries, preset, customFrom, customTo, 'created_date');
    // Aggregate by source
    const bySource = {};
    for (const e of enq) {
      const s = e.source || 'other';
      if (!bySource[s]) bySource[s] = { source: s, total: 0, converted: 0, dropped: 0, spend: 0 };
      bySource[s].total += 1;
      if (e.status === 'converted') bySource[s].converted += 1;
      if (e.status === 'dropped' || e.status === 'lost') bySource[s].dropped += 1;
    }
    // Spend for paid channels
    const campSpend = camps.reduce((s, c) => s + Number(c.actual_spend || c.budget || 0), 0);
    const marketingSpend = campaigns.reduce((s, c) => s + Number(c.spend || 0), 0);
    if (bySource['health_camp'])       bySource['health_camp'].spend = campSpend;
    if (bySource['digital_marketing']) bySource['digital_marketing'].spend = marketingSpend;

    return Object.values(bySource).sort((a, b) => b.total - a.total);
  }, [enquiries, camps, campaigns, preset, customFrom, customTo]);

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/reports')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Reports
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-sm">
          <IndianRupee className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Source ROI</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Spend vs conversions per channel</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Source</th>
              <th className="px-4 py-2 text-right font-medium">Total</th>
              <th className="px-4 py-2 text-right font-medium">Converted</th>
              <th className="px-4 py-2 text-right font-medium">Dropped</th>
              <th className="px-4 py-2 text-right font-medium">Conv. %</th>
              <th className="px-4 py-2 text-right font-medium">Spend (₹)</th>
              <th className="px-4 py-2 text-right font-medium">Cost / patient</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
            : rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No data.</td></tr>
            : rows.map(r => {
              const convPct = r.total ? Math.round(r.converted / r.total * 100) : 0;
              const cpp = r.converted > 0 && r.spend > 0 ? r.spend / r.converted : 0;
              return (
                <tr key={r.source} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5"><span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(r.source))}>
                    {ENGAGE_SOURCE_LABEL[r.source] || r.source}
                  </span></td>
                  <td className="px-4 py-2.5 text-right font-medium text-gray-900 dark:text-white">{r.total}</td>
                  <td className="px-4 py-2.5 text-right text-green-600 dark:text-green-400">{r.converted}</td>
                  <td className="px-4 py-2.5 text-right text-red-500">{r.dropped}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium',
                      convPct >= 30 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      convPct >= 15 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300')}>
                      {convPct}%
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">{r.spend > 0 ? `₹ ${formatINR(r.spend)}` : '—'}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-amber-600 dark:text-amber-400">{cpp > 0 ? `₹ ${formatINR(cpp)}` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
